import { FieldPath, FieldValues, useController } from "react-hook-form";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import React, { useMemo, useState } from "react";
import { debounce } from "lodash";
import axios from "axios";
import { toast } from "react-toastify";
import { SelectionListItem } from "../../Forms/SelectionListItem";
import { RhfInputProps } from "../../Forms/RhfInputProps";
import { OptionalLabelProps } from "../../Forms/OptionalLabelProps";
import { RHFFieldErrorText } from "../../Forms/RHFFieldErrorText";
import { isOptionEqualsValue } from "../../Utils/strings";
import { disabledMuiStyle } from "../../MaterialUi/disabledMuiStyle";

type Props = {
  initialSelectionItems: SelectionListItem<string>[];
  apiUrl: string;
  errorMessage: string;
  disabled: boolean;
};

export const RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  initialSelectionItems,
  apiUrl,
  errorMessage,
  label,
  disabled,
}: RhfInputProps<TFieldValues, TName> & Props & OptionalLabelProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const { onChange: onAutoCompleteChange } = field;
  const [itemsFromSearch, setItemsFromSearch] =
    useState<SelectionListItem<string>[]>(initialSelectionItems);

  const fetchItemsDebounced = useMemo(
    () =>
      debounce(async (search: string) => {
        if (!search) return;
        await axios
          .get<SelectionListItem<string>[]>(apiUrl, {
            params: { search },
          })
          .then((response) => {
            setItemsFromSearch(response.data);
          })
          .catch(() => {
            toast.error(errorMessage);
          });
      }, 750),
    [apiUrl, errorMessage]
  );

  const getLabelFor = (
    itemId: string,
    initialItems: SelectionListItem<string>[],
    itemsSearch: SelectionListItem<string>[]
  ) => {
    const concat = initialItems.concat(itemsSearch);
    return concat.find((it) => it.Id === itemId)?.Label || "NOT FOUND";
  };

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        {...field}
        noOptionsText="Please start typing to search for options"
        options={itemsFromSearch.map((it) => it.Id)}
        getOptionLabel={(itemId) => getLabelFor(itemId, initialSelectionItems, itemsFromSearch)}
        isOptionEqualToValue={(option, value) => isOptionEqualsValue(option, value)}
        onChange={(_, selectedValue) => onAutoCompleteChange(selectedValue)}
        onInputChange={(_, value, reason) => {
          if (reason === "input") fetchItemsDebounced(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputLabelProps={{ shrink: true }}
            label={label}
            inputProps={{
              ...params.inputProps,
              readOnly: disabled,
              style: { ...disabledMuiStyle(disabled) },
            }}
          />
        )}
        size="small"
        filterSelectedOptions
      />
      {!!error && <RHFFieldErrorText fieldError={error} />}
    </FormControl>
  );
};
